import { Injectable } from '@angular/core';
import DOMPurify from 'dompurify';

@Injectable({
  providedIn: 'root'
})
export class SanitizationService {

  /**
   * Sanitiza un string para prevenir XSS attacks
   * @param value String a sanitizar
   * @returns String sanitizado
   */
  sanitize(value: string): string {
    if (!value) {
      return '';
    }
    return DOMPurify.sanitize(value, { 
      ALLOWED_TAGS: [], // No permitir ningún tag HTML
      KEEP_CONTENT: true // Mantener el contenido del texto
    });
  }

  /**
   * Sanitiza un string permitiendo algunos tags HTML seguros
   * Útil para contenido enriquecido controlado
   * @param value String a sanitizar
   * @returns String sanitizado con tags seguros
   */
  sanitizeHtml(value: string): string {
    if (!value) {
      return '';
    }
    return DOMPurify.sanitize(value, {
      ALLOWED_TAGS: ['b', 'i', 'em', 'strong', 'u', 'p', 'br'],
      ALLOWED_ATTR: []
    });
  }

  /**
   * Sanitiza un objeto recursivamente
   * @param obj Objeto a sanitizar
   * @returns Objeto sanitizado
   */
  sanitizeObject<T>(obj: T): T {
    if (obj === null || obj === undefined) {
      return obj;
    }

    if (typeof obj === 'string') {
      return this.sanitize(obj) as any;
    }

    if (Array.isArray(obj)) {
      return obj.map(item => this.sanitizeObject(item)) as any;
    }

    if (typeof obj === 'object') {
      const sanitized: any = {};
      for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
          sanitized[key] = this.sanitizeObject(obj[key]);
        }
      }
      return sanitized;
    }

    return obj;
  }

  /**
   * Valida y sanitiza un email
   * @param email Email a validar
   * @returns Email sanitizado o string vacío si es inválido
   */
  sanitizeEmail(email: string): string {
    if (!email) {
      return '';
    }
    
    const sanitized = this.sanitize(email.toLowerCase().trim());
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    
    return emailRegex.test(sanitized) ? sanitized : '';
  }

  /**
   * Sanitiza un número de teléfono (solo dígitos, +, -, paréntesis, espacios)
   * @param phone Teléfono a sanitizar
   * @returns Teléfono sanitizado
   */
  sanitizePhone(phone: string): string {
    if (!phone) {
      return '';
    }
    return phone.replace(/[^0-9+\-() ]/g, '').trim();
  }

  /**
   * Sanitiza una URL
   * @param url URL a sanitizar
   * @returns URL sanitizada o string vacío si es inválida
   */
  sanitizeUrl(url: string): string {
    if (!url) {
      return '';
    }
    
    const sanitized = this.sanitize(url.trim());
    
    // Solo permitir URLs con protocolos seguros
    const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;
    
    if (!urlRegex.test(sanitized)) {
      return '';
    }
    
    // Asegurar que comience con http o https
    if (!sanitized.startsWith('http://') && !sanitized.startsWith('https://')) {
      return 'https://' + sanitized;
    }
    
    return sanitized;
  }
}

